"use client";

import React, { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { fetchTopCategories } from "@/api-service/category";
import { TopCategoryItem } from "@/packages/types/category";
import { useFastRouter } from "@/packages/hooks/useFastRouter";
import NextImage from "../common/NextImage";
import { Swiper, SwiperSlide } from "swiper/react";
import { Keyboard } from "swiper/modules";
import { Swiper as SwiperType } from "swiper";
import { ChevronLeft, ChevronRight } from "lucide-react";

import "swiper/css";

import { HEADER_V2_DESIGN_SYSTEM } from "../header/v2/designTokens";
import { useResolvedCountryName } from "@/packages/hooks/useResolvedCountryName";
import { CategoryCardSkeleton } from "../common/Skeletons";

const CategoryWithSlider = () => {
  const countryName = useResolvedCountryName();
  const [categories, setCategories] = useState<TopCategoryItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [swiperInstance, setSwiperInstance] = useState<SwiperType | null>(null);
  const [isBeginning, setIsBeginning] = useState(true);
  const [isEnd, setIsEnd] = useState(false);
  const { fastPush } = useFastRouter();

  useEffect(() => {
    let isMounted = true;
    const loadCategories = async () => {
      try {
        setLoading(true);
        const res = await fetchTopCategories();
        if (isMounted) {
          if (res.status === "SUCCESS") {
            setCategories(res.data || []);
          } else {
            toast.error(res.message || "Failed to load categories");
          }
        }
      } catch (error: unknown) {
        console.error("Error fetching top categories:", error);
        if (isMounted) {
          toast.error("Error fetching top categories");
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    loadCategories();
    return () => {
      isMounted = false;
    };
  }, []);

  const handlePrev = useCallback(() => {
    swiperInstance?.slidePrev();
  }, [swiperInstance]);

  const handleNext = useCallback(() => {
    swiperInstance?.slideNext();
  }, [swiperInstance]);

  const handleSwiperInit = useCallback((swiper: SwiperType) => {
    setSwiperInstance(swiper);
    setIsBeginning(swiper.isBeginning);
    setIsEnd(swiper.isEnd);
  }, []);

  const handleSlideChange = useCallback((swiper: SwiperType) => {
    setIsBeginning(swiper.isBeginning);
    setIsEnd(swiper.isEnd);
  }, []);

  if (!loading && categories.length === 0) {
    return null;
  }

  return (
    <section className={`w-full max-w-7xl mx-auto my-6 md:my-8 ${HEADER_V2_DESIGN_SYSTEM.spacing.containerPadding}`}>
      {loading ? (
        <div className="w-full">
          <div className="h-6 w-44 bg-slate-200 rounded mb-6 animate-pulse" />
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
            {Array.from({ length: 5 }).map((_, i) => (
              <CategoryCardSkeleton key={i} />
            ))}
          </div>
        </div>
      ) : (
        <div className="relative w-full group">
          {categories.length > 0 && (
            <h2 className={`font-semibold tracking-tight ${HEADER_V2_DESIGN_SYSTEM.typography.fontFamilyHeading} ${HEADER_V2_DESIGN_SYSTEM.typography.size20} ${HEADER_V2_DESIGN_SYSTEM.colors.primaryDarkText} mb-6 md:mb-8`}>
              Category Spotlight{countryName ? ` in ${countryName}` : ""}
            </h2>
          )}

          {/* Swiper Slider Wrapper */}
          <div className="relative w-full px-1 py-1">
            <Swiper
              modules={[Keyboard]}
              spaceBetween={12}
              slidesPerView={1.8}
              keyboard={{ enabled: true }}
              onSwiper={handleSwiperInit}
              onSlideChange={handleSlideChange}
              breakpoints={{
                480: { slidesPerView: 2.2, spaceBetween: 12 },
                640: { slidesPerView: 2.8, spaceBetween: 12 },
                768: { slidesPerView: 3.4, spaceBetween: 12 },
                1024: { slidesPerView: 4.4, spaceBetween: 12 },
              }}
              className="w-full !overflow-visible md:!overflow-hidden"
            >
              {categories.map((cat, index) => (
                <SwiperSlide key={cat.id || index} className="h-auto">
                  {/* Perfect aspect-square category card */}
                  <div
                    onClick={() => fastPush(`/collections/${cat.category.slug}`)}
                    className={`relative w-full aspect-square overflow-hidden cursor-pointer group 
                      ${HEADER_V2_DESIGN_SYSTEM.borderRadius.card} 
                      ${HEADER_V2_DESIGN_SYSTEM.elevation.card} 
                      ${HEADER_V2_DESIGN_SYSTEM.elevation.cardHover} transition-all duration-300`}
                  >
                    {/* Full-width bottom translucent glass name bar */}
                    <div className="absolute bottom-0 left-0 right-0 w-full z-10 pointer-events-none bg-white/45 backdrop-blur-md py-2.5 px-3 text-center border-t border-white/30">
                      <p
                        className={`text-xs md:text-sm font-bold tracking-tight leading-tight text-slate-900 truncate capitalize 
                        ${HEADER_V2_DESIGN_SYSTEM.typography.fontFamilySans}`}
                      >
                        {cat?.category?.name}
                      </p>
                    </div>

                    <NextImage
                      src={
                        cat?.category?.imageUrl?.trim() ||
                        "/assets/site/images/default.png"
                      }
                      alt={
                        cat?.category?.name
                          ? `${cat.category.name}${countryName ? ` in ${countryName}` : ""}`
                          : "Category Image"
                      }
                      fill
                      sizes="(max-width: 640px) 50vw, (max-width: 1024px) 25vw, 20vw"
                      className="object-cover transition-transform duration-500 ease-out group-hover:scale-103"
                      priority={index < 5}
                    />
                  </div>
                </SwiperSlide>
              ))}
            </Swiper>

            {/* Custom Rectangular Translucent Navigation Buttons */}
            {categories.length > 2 && (
              <>
                {/* Prev Button */}
                <button
                  onClick={(e) => {
                    e.preventDefault();
                    handlePrev();
                  }}
                  disabled={isBeginning}
                  className={`absolute left-0 top-1/2 -translate-y-1/2 z-10 
                    hidden md:flex items-center justify-center w-8 h-16 rounded-r-md 
                    bg-black/15 hover:bg-black/25 text-white transition-all duration-300 
                    focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#D97706] 
                    disabled:opacity-30 disabled:pointer-events-none`}
                  aria-label="Previous slide"
                >
                  <ChevronLeft className="w-5 h-5 stroke-[2.5]" />
                </button>

                {/* Next Button */}
                <button
                  onClick={(e) => {
                    e.preventDefault();
                    handleNext();
                  }}
                  disabled={isEnd}
                  className={`absolute right-0 top-1/2 -translate-y-1/2 z-10 
                    hidden md:flex items-center justify-center w-8 h-16 rounded-l-md 
                    bg-black/15 hover:bg-black/25 text-white transition-all duration-300 
                    focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#D97706] 
                    disabled:opacity-30 disabled:pointer-events-none`}
                  aria-label="Next slide"
                >
                  <ChevronRight className="w-5 h-5 stroke-[2.5]" />
                </button>
              </>
            )}
          </div>
        </div>
      )}
    </section>
  );
};

export default CategoryWithSlider;
